import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import { lerContexto } from './api';
import { contextosDe } from './contexto';
import { Landing } from './telas/Landing';
import { Entrar } from './telas/Entrar';
import { Codigo } from './telas/Codigo';
import { Central } from './telas/Central';
import { EscolherContexto } from './telas/EscolherContexto';
import { Casca } from './telas/Casca';
import { VisaoGeral } from './telas/admin/VisaoGeral';
import { Escritorios } from './telas/admin/Escritorios';
import { Pendencias } from './telas/admin/Pendencias';
import { Documentos } from './telas/Documentos';
import { Empresas } from './telas/admin/Empresas';
import { Instalacoes } from './telas/admin/Instalacoes';
import { Pessoas } from './telas/admin/Pessoas';
import { ConfiguracoesSistema } from './telas/admin/ConfiguracoesSistema';
import { Comissoes } from './telas/admin/Comissoes';
import { Indicacoes } from './telas/Indicacoes';
import { Chamados } from './telas/Chamados';
import { Carregando } from './componentes/ui';
import type { ReactNode } from 'react';

function Protegida({ children, admin }: { children: ReactNode; admin?: boolean }) {
  const { usuario, carregando } = useAuth();
  const { pathname } = useLocation();
  if (carregando) return <div className="center-screen"><Carregando /></div>;
  if (!usuario) return <Navigate to="/entrar" replace />;

  // Mais de uma conta no mesmo celular e ninguém escolheu ainda: pergunta
  // antes de mostrar qualquer painel. /escolher é a própria pergunta, não
  // entra nesse laço.
  const contexto = lerContexto();
  if (pathname !== '/escolher' && !contexto && contextosDe(usuario).length > 1) {
    return <Navigate to="/escolher" replace />;
  }

  const ehAdmin = contexto ? contexto.tipo === 'admin' : usuario.perfis.includes('admin');
  if (admin && !ehAdmin) return <Navigate to="/painel" replace />;
  return <>{children}</>;
}

function SoDeslogado({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useAuth();
  if (carregando) return <div className="center-screen"><Carregando /></div>;
  if (usuario) return <Navigate to="/painel" replace />;
  return <>{children}</>;
}

/**
 * O admin começa pela visão geral; contador e cliente, pelos documentos;
 * suporte (softhouse) começa direto nos chamados, que é a única coisa que
 * ele enxerga antes de assumir algum. Olha o CONTEXTO escolhido, não só o
 * que a pessoa é de verdade -- quem entrou como contador vê a tela de
 * contador, mesmo sendo admin também.
 */
function Inicio() {
  const { usuario } = useAuth();
  const contexto = lerContexto();
  if (contexto?.tipo === 'softhouse') return <Chamados />;
  const ehAdmin = contexto ? contexto.tipo === 'admin' : (usuario?.perfis.includes('admin') ?? false);
  return ehAdmin ? <VisaoGeral /> : <Documentos />;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/entrar" element={<SoDeslogado><Entrar /></SoDeslogado>} />
      <Route path="/codigo" element={<Codigo />} />
      <Route path="/central" element={<Central />} />
      <Route path="/escolher" element={<Protegida><EscolherContexto /></Protegida>} />

      <Route path="/painel" element={<Protegida><Casca /></Protegida>}>
        <Route index element={<Inicio />} />
        <Route path="documentos" element={<Documentos />} />
        <Route path="pendencias" element={<Protegida admin><Pendencias /></Protegida>} />
        <Route path="escritorios" element={<Protegida admin><Escritorios /></Protegida>} />
        <Route path="empresas" element={<Protegida admin><Empresas /></Protegida>} />
        <Route path="instalacoes" element={<Protegida admin><Instalacoes /></Protegida>} />
        <Route path="pessoas" element={<Protegida admin><Pessoas /></Protegida>} />
        <Route path="config" element={<Protegida admin><ConfiguracoesSistema /></Protegida>} />
        <Route path="indicacoes" element={<Indicacoes />} />
        <Route path="comissoes" element={<Protegida admin><Comissoes /></Protegida>} />
        <Route path="chamados" element={<Chamados />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
